import {
    Badge,
    Button,
    Card,
    ExitCandidateSelect,
    FormNumberInput,
    FormSelect,
    SegmentedControl,
    TradingStrategySelect
} from '@/components/UI';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import type {
    ExperimentDurationSummary,
    ExperimentExecutionStatus,
    OptimizerPreflight,
    OptionObjective,
    OutcomeMode,
    OutcomeMetric,
    SupportedEntityType
} from './types';
import { formatPct01Number, helpTextForOptionObjective } from './helpers';
import OptionLegConfigFields from './OptionLegConfigFields';
import ValidationConfigFields from './ValidationConfigFields';
import { optionLegFields, type OptimizerOptionOverrides, type OptimizerValidationSettings, type OptionLegField } from './jobConfigInput';

const formatShortFingerprint = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const normalized = value.startsWith('sha256:') ? value.slice('sha256:'.length) : value;
    return normalized.length > 16 ? `${normalized.slice(0, 16)}...` : normalized;
};

interface JobConfigCardProps {
    supportedEntityTypes: SupportedEntityType[];
    durations: ExperimentDurationSummary[];
    availableHorizons: number[];
    entityType: SupportedEntityType;
    setEntityType: (value: SupportedEntityType) => void;
    duration: string;
    setDuration: (value: string) => void;
    horizonSeconds: number;
    setHorizonSeconds: (value: number) => void;
    outcomeMode: OutcomeMode;
    setOutcomeMode: (value: OutcomeMode) => void;
    optionLegOverrides: OptimizerOptionOverrides;
    onOptionLegChange: (key: OptionLegField, value: number | null) => void;
    validationSettings: OptimizerValidationSettings;
    onValidationSettingsChange: (value: OptimizerValidationSettings) => void;
    outcomeMetric: OutcomeMetric;
    setOutcomeMetric: (value: OutcomeMetric) => void;
    optionObjective: OptionObjective;
    setOptionObjective: (value: OptionObjective) => void;
    strategyExitStrategyId: string;
    setStrategyExitStrategyId: (value: string) => void;
    strategyExitCandidateId: string;
    setStrategyExitCandidateId: (value: string) => void;
    showAdvancedConfig: boolean;
    setShowAdvancedConfig: (value: boolean) => void;
    maxDepth: number;
    setMaxDepth: (value: number) => void;
    binCount: number;
    setBinCount: (value: number) => void;
    minSupport: number;
    setMinSupport: (value: number) => void;
    minUniqueEntities: number;
    setMinUniqueEntities: (value: number) => void;
    foldCount: number;
    setFoldCount: (value: number) => void;
    maxFeatures: number;
    setMaxFeatures: (value: number) => void;
    executionStatus: ExperimentExecutionStatus;
    preflightEnabled: boolean;
    preflightIsFetching: boolean;
    preflightIsError: boolean;
    preflightErrorMessage: string | undefined;
    preflight: OptimizerPreflight | null;
    canCreateJob: boolean;
    createJobPending: boolean;
    onCreateJob: () => void;
}

const JobConfigCard = ({
    supportedEntityTypes,
    durations,
    availableHorizons,
    entityType,
    setEntityType,
    duration,
    setDuration,
    horizonSeconds,
    setHorizonSeconds,
    outcomeMode,
    setOutcomeMode,
    optionLegOverrides,
    onOptionLegChange,
    validationSettings,
    onValidationSettingsChange,
    outcomeMetric,
    setOutcomeMetric,
    optionObjective,
    setOptionObjective,
    strategyExitStrategyId,
    setStrategyExitStrategyId,
    strategyExitCandidateId,
    setStrategyExitCandidateId,
    showAdvancedConfig,
    setShowAdvancedConfig,
    maxDepth,
    setMaxDepth,
    binCount,
    setBinCount,
    minSupport,
    setMinSupport,
    minUniqueEntities,
    setMinUniqueEntities,
    foldCount,
    setFoldCount,
    maxFeatures,
    setMaxFeatures,
    executionStatus,
    preflightEnabled,
    preflightIsFetching,
    preflightIsError,
    preflightErrorMessage,
    preflight,
    canCreateJob,
    createJobPending,
    onCreateJob
}: JobConfigCardProps) => {
    const resolvedExitFingerprint =
        preflight?.strategyExit?.exitFingerprint ?? preflight?.outcomeModeResolution.resolvedExitFingerprint ?? null;

    const resolvedExitFingerprintLabel = formatShortFingerprint(resolvedExitFingerprint);

    return (
        <Card className="bg-zinc-900 mb-6">
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-white text-lg font-semibold">Screener Optimizer</h3>
                        <Badge variant="info">Exhaustive</Badge>
                    </div>
                    <div className="text-zinc-400 text-sm mt-1">
                        Exhaustive combinatorial search over screener features with cross-validation and holdout validation.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant={showAdvancedConfig ? 'primary' : 'secondary'}
                        aria-expanded={showAdvancedConfig}
                        aria-controls="optimizer-advanced-config"
                        onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                    >
                        Config
                    </Button>
                    <Button disabled={!canCreateJob} variant="primary" onClick={onCreateJob}>
                        {createJobPending
                            ? 'Creating...'
                            : preflightEnabled && preflightIsFetching
                              ? 'Checking Dataset...'
                              : 'Create Optimizer Job'}
                    </Button>
                </div>
            </div>

            {supportedEntityTypes.length === 0 ? (
                <div className="text-zinc-400 text-sm">
                    Optimizer supports stock, index, and cryptocurrency experiments. No supported entity types were found.
                </div>
            ) : (
                <>
                    <div className="mb-4">
                        <div className="text-sm font-medium text-zinc-300 mb-2">Outcome Mode</div>
                        <SegmentedControl<OutcomeMode>
                            options={[
                                { value: 'fixed_horizon', label: 'Fixed Horizon' },
                                { value: 'strategy_exit', label: 'Strategy Exit' }
                            ]}
                            value={outcomeMode}
                            onChange={setOutcomeMode}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <FormSelect
                            id="opt-entity-type"
                            label="Entity Type"
                            options={supportedEntityTypes.map((t) => ({ value: t, label: t }))}
                            value={entityType}
                            onChange={(e) => setEntityType(e.target.value as SupportedEntityType)}
                        />
                        <FormSelect
                            id="opt-duration"
                            label="Duration"
                            options={durations.map((d) => ({
                                value: d.duration,
                                label: `${d.duration} (${d.totalEntries} entries)`
                            }))}
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                        />
                        <FormSelect
                            id="opt-horizon"
                            label="Time Horizon"
                            options={availableHorizons.map((h) => ({
                                value: h,
                                label: formatTimeHorizon(h)
                            }))}
                            value={horizonSeconds}
                            onChange={(e) => setHorizonSeconds(Number(e.target.value))}
                        />
                        <FormSelect
                            id="opt-outcome-metric"
                            label="Outcome Metric"
                            options={[
                                { value: 'underlying_profit_percent', label: 'Underlying P&L' },
                                {
                                    value: 'option_profit_percent',
                                    label: 'Options P&L',
                                    disabled: entityType === 'Cryptocurrency'
                                }
                            ]}
                            value={outcomeMetric}
                            onChange={(e) => setOutcomeMetric(e.target.value as OutcomeMetric)}
                        />
                        {outcomeMetric === 'option_profit_percent' && (
                            <FormSelect
                                helpText={helpTextForOptionObjective(optionObjective)}
                                id="opt-option-objective"
                                label="Option Objective"
                                options={[
                                    { value: 'ev_per_signal', label: 'EV per signal' },
                                    { value: 'executed_only', label: 'Executed options only' }
                                ]}
                                value={optionObjective}
                                onChange={(e) => setOptionObjective(e.target.value as OptionObjective)}
                            />
                        )}
                    </div>

                    {outcomeMode === 'strategy_exit' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-zinc-800">
                            <TradingStrategySelect
                                helpText="Defaults to the experiment strategy when available."
                                id="opt-strategy-exit-strategy-id"
                                label="Strategy"
                                value={strategyExitStrategyId ? Number(strategyExitStrategyId) : null}
                                onChange={(strategyId) => setStrategyExitStrategyId(strategyId === null ? '' : String(strategyId))}
                            />
                            <ExitCandidateSelect
                                helpText="Active global exit candidate to replay."
                                id="opt-strategy-exit-candidate-id"
                                label="Global exit candidate"
                                mode="single"
                                placeholder="Search global candidates…"
                                value={strategyExitCandidateId ? Number(strategyExitCandidateId) : null}
                                onChange={(candidateId) => setStrategyExitCandidateId(candidateId === null ? '' : String(candidateId))}
                            />
                        </div>
                    )}

                    {outcomeMode === 'strategy_exit' && entityType !== 'Cryptocurrency' && (
                        <OptionLegConfigFields
                            strategyId={strategyExitStrategyId ? Number(strategyExitStrategyId) : null}
                            overrides={optionLegOverrides}
                            onChange={onOptionLegChange}
                            preflight={preflight}
                        />
                    )}

                    {showAdvancedConfig && (
                        <div
                            id="optimizer-advanced-config"
                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-zinc-800"
                        >
                            <FormNumberInput
                                integer
                                helpText="Max conditions per rule (1-6)"
                                id="opt-max-depth"
                                label="Max Depth"
                                max={6}
                                min={1}
                                value={maxDepth}
                                onValueChange={(value) => {
                                    // The setting always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setMaxDepth(value);
                                }}
                            />
                            <FormNumberInput
                                integer
                                helpText="Quantile bins per feature (5-50)"
                                id="opt-bin-count"
                                label="Bin Count"
                                max={50}
                                min={5}
                                value={binCount}
                                onValueChange={(value) => {
                                    // The setting always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setBinCount(value);
                                }}
                            />
                            <FormNumberInput
                                integer
                                helpText="Min passing entries (10-10000)"
                                id="opt-min-support"
                                label="Min Support"
                                max={10000}
                                min={10}
                                value={minSupport}
                                onValueChange={(value) => {
                                    // The setting always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setMinSupport(value);
                                }}
                            />
                            <FormNumberInput
                                integer
                                helpText="Minimum unique entities required by validation (1-1000)"
                                id="opt-min-unique-entities"
                                label="Min Unique Entities"
                                max={1000}
                                min={1}
                                value={minUniqueEntities}
                                onValueChange={(value) => {
                                    // The setting always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setMinUniqueEntities(value);
                                }}
                            />
                            <FormNumberInput
                                integer
                                helpText="Cross-validation folds (2-20)"
                                id="opt-fold-count"
                                label="CV Folds"
                                max={20}
                                min={2}
                                value={foldCount}
                                onValueChange={(value) => {
                                    // The setting always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setFoldCount(value);
                                }}
                            />
                            <FormNumberInput
                                integer
                                helpText="Top-K features for combinatorial search (5-200). Higher = slower but more inclusive."
                                id="opt-max-features"
                                label="Max Features"
                                max={200}
                                min={5}
                                value={maxFeatures}
                                onValueChange={(value) => {
                                    // The setting always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setMaxFeatures(value);
                                }}
                            />
                            <ValidationConfigFields value={validationSettings} onChange={onValidationSettingsChange} />
                        </div>
                    )}

                    {executionStatus !== 'completed' && (
                        <div className="text-yellow-200 text-sm mt-3">
                            Experiment must be completed before the optimizer can run (executionStatus={executionStatus}).
                        </div>
                    )}

                    {preflightEnabled && preflightIsFetching && !preflight && (
                        <div className="text-zinc-400 text-sm mt-3">Checking optimizer dataset...</div>
                    )}

                    {preflightIsError && (
                        <div className="border-l-2 border-red-500 bg-red-950/30 px-3 py-2 text-red-200 text-sm mt-3">
                            Failed to check optimizer dataset: {preflightErrorMessage ?? 'Unknown error'}
                        </div>
                    )}

                    {preflight && (
                        <div className="text-zinc-400 text-xs mt-3">
                            Optimizer dataset: {preflight.dataset.eligibleEntryCount.toLocaleString()} entries |{' '}
                            {preflight.dataset.eligibleEntityCount.toLocaleString()} entities | min entries{' '}
                            {preflight.config.minSupport.toLocaleString()} | min entities{' '}
                            {preflight.config.minUniqueEntities.toLocaleString()} | feature set {preflight.config.featureSetVersion}
                            {preflight.config.outcomeMode === 'strategy_exit' && preflight.strategyExit && (
                                <>
                                    {' '}
                                    | strategy-exit{' '}
                                    {preflight.strategyExit.outcomeSetStatus ?? (preflight.strategyExit.outcomeSetId ? 'ready' : 'new')}
                                    {preflight.strategyExit.outcomeSetId ? ` #${preflight.strategyExit.outcomeSetId}` : ''}
                                    {resolvedExitFingerprintLabel ? ` | exit fp ${resolvedExitFingerprintLabel}` : ''}
                                    {optionLegFields.map((field) => {
                                        const id = preflight.outcomeModeResolution.resolvedOptionLegStrategyIds?.[field.resolvedKey];
                                        return id ? <span key={field.key}>{` | leg ${field.number} option strategy #${id}`}</span> : null;
                                    })}
                                </>
                            )}
                            {outcomeMetric === 'option_profit_percent' && (
                                <>
                                    {' '}
                                    | objective {optionObjective === 'ev_per_signal' ? 'EV per signal' : 'executed only'} | option-labeled{' '}
                                    {preflight.dataset.optionOutcomeEntryCount.toLocaleString()}/
                                    {preflight.dataset.optionOpportunityEntryCount.toLocaleString()} (
                                    {formatPct01Number(preflight.dataset.optionCoverage, 1)})
                                </>
                            )}
                        </div>
                    )}

                    {preflight?.outcomeModeResolution.autoDefaulted &&
                        preflight.outcomeModeResolution.recommendedOutcomeMode === 'strategy_exit' &&
                        preflight.outcomeModeResolution.resolvedExitCandidateId !== null && (
                            <div className="border-l-2 border-emerald-500 bg-emerald-950/30 px-3 py-2 text-emerald-100 text-xs mt-3">
                                Option strategy detected: defaulted to Strategy Exit (realized objective) using active exit candidate #
                                {preflight.outcomeModeResolution.resolvedExitCandidateId}
                                {preflight.outcomeModeResolution.resolvedStrategyId !== null
                                    ? ` for strategy ${preflight.outcomeModeResolution.resolvedStrategyId}`
                                    : ''}
                                {resolvedExitFingerprintLabel ? ` (fp ${resolvedExitFingerprintLabel})` : ''}. Switch to Fixed Horizon for a
                                fast entry-edge scan (not promotable for options).
                            </div>
                        )}

                    {preflight && preflight.blockers.length > 0 && (
                        <div className="border-l-2 border-red-500 bg-red-950/30 px-3 py-2 text-red-200 text-sm mt-3">
                            <div className="font-medium text-red-100">Optimizer job cannot run with this dataset.</div>
                            <ul className="list-disc pl-5 mt-1 space-y-1">
                                {preflight.blockers.map((blocker) => (
                                    <li key={blocker}>{blocker}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {preflight?.blockers.length === 0 && preflight.warnings.length > 0 && (
                        <div className="border-l-2 border-yellow-500 bg-yellow-950/30 px-3 py-2 text-yellow-100 text-sm mt-3">
                            <ul className="list-disc pl-5 space-y-1">
                                {preflight.warnings.map((warning) => (
                                    <li key={warning}>{warning}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="text-zinc-500 text-xs mt-2">
                        Optimizer jobs run via the backend job runner (cron/systemd). Newly created jobs may remain queued for a few
                        minutes.
                    </div>
                </>
            )}
        </Card>
    );
};

export default JobConfigCard;
